import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  MessageCircle,
  BookOpen,
  FileText,
  Clock,
  Sparkles
} from 'lucide-react';
import { Course, StudySession, StudyMaterial, coursesApi, studySessionsApi, aiApi, authApi } from '../services/api';
import type { ChatMode } from '../services/api';
import { analytics } from '../services/analytics';
import ChatWidget from './study-session/ChatWidget';
import { MixedText } from '../utils/rtl';
import { Card, CardHeader, CardTitle, CardContent } from './ui/Card';
import { LoadingSpinner } from './ui/LoadingSpinner';
import { PageContainer } from './layout/PageContainer';
import StudyMaterialRenderer from './StudyMaterialRenderer';

const CourseChatView: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'he';
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();

  // State
  const [course, setCourse] = useState<Course | null>(null);
  const [sessions, setSessions] = useState<StudySession[]>([]);
  const [allMaterials, setAllMaterials] = useState<StudyMaterial[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Materials are always shown, no toggle needed
  const [selectedMaterial, setSelectedMaterial] = useState<StudyMaterial | null>(null);

  // Chat state
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [chatHistory, setChatHistory] = useState<Array<{type: 'user' | 'ai', message: string}>>([]);
  const [messageFeedback, setMessageFeedback] = useState<{[messageIndex: number]: 'like' | 'dislike' | null}>({});
  const [chatMode, setChatMode] = useState<ChatMode>('fast');
  const [isVip, setIsVip] = useState(() => {
    try {
      const rawUser = localStorage.getItem('user');
      if (!rawUser) return false;
      return Boolean(JSON.parse(rawUser)?.is_vip);
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (isVip) {
      setChatMode('smart');
    }
  }, [isVip]);

  useEffect(() => {
    if (courseId) {
      loadCourseData();
    }
  }, [courseId]);

  const loadCourseData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Load user data for VIP status
      let vip = isVip;
      try {
        const userData = await authApi.getCurrentUser();
        vip = Boolean(userData?.is_vip);
        setIsVip(vip);
      } catch (err) {
        console.error('Failed to load user data for VIP status:', err);
      }

      // Load course data
      const courseData = await coursesApi.getCourse(parseInt(courseId!));
      setCourse(courseData);

      // Load all sessions for the course
      const sessionsData = await coursesApi.getCourseSessions(parseInt(courseId!));
      setSessions(sessionsData);

      // Load all study materials from all sessions
      const materials: StudyMaterial[] = [];
      for (const session of sessionsData) {
        if (session.study_materials && session.study_materials.length > 0) {
          materials.push(...session.study_materials);
        }
      }
      setAllMaterials(materials);

      // Load chat history if available
      try {
        const historyResponse = await aiApi.getCourseChatHistory(parseInt(courseId!));
        if (historyResponse.history && historyResponse.history.length > 0) {
          setChatHistory(historyResponse.history);
        }
        if (historyResponse.mode) {
          setChatMode(vip ? 'smart' : (historyResponse.mode as ChatMode));
        }
      } catch (error) {
        console.error('Failed to load chat history:', error);
      }

      analytics.track('Course Chat Opened', {
        course_id: parseInt(courseId!),
        total_sessions: sessionsData.length,
        total_materials: materials.length
      });
    } catch (err: any) {
      console.error('Error loading course data:', err);
      setError(t('errors:something_went_wrong'));
    } finally {
      setLoading(false);
    }
  };

  // Chat handlers
  const handleChatUpdate = useCallback((message: {type: 'user' | 'ai', message: string}) => {
    setChatHistory(prev => [...prev, message]);
  }, []);

  const handleMessageFeedback = useCallback((messageIndex: number, feedback: 'like' | 'dislike') => {
    setMessageFeedback(prev => ({
      ...prev,
      [messageIndex]: prev[messageIndex] === feedback ? null : feedback
    }));
  }, []);

  // Custom chat handler for course-specific chat
  const customChatHandler = useCallback(async (message: string, mode: ChatMode) => {
    if (!course) {
      throw new Error('Course not available');
    }

    try {
      const effectiveMode: ChatMode = isVip ? 'smart' : mode;
      const response = await aiApi.chatWithCourseMaterials(
        parseInt(courseId!),
        message,
        effectiveMode
      );

      if (response.mode) {
        setChatMode(isVip ? 'smart' : (response.mode as ChatMode));
      }

      return { response: response.response };
    } catch (error) {
      console.error('Course chat error:', error);
      throw error;
    }
  }, [course, courseId, isVip]);

  const handleChatModeChange = useCallback(async (newMode: ChatMode) => {
    const previousMode = chatMode;
    setChatMode(newMode);
    if (!courseId) {
      return;
    }
    try {
      const historyResponse = await aiApi.getCourseChatHistory(parseInt(courseId), newMode);
      if (historyResponse.history && historyResponse.history.length > 0) {
        setChatHistory(historyResponse.history);
      } else {
        setChatHistory([]);
      }
      if (historyResponse.mode) {
        setChatMode(historyResponse.mode as ChatMode);
      }
    } catch (error) {
      console.error('Failed to switch course chat mode:', error);
      setChatMode(previousMode);
      throw error;
    }
  }, [courseId, chatMode]);

  const formatDate = (dateString: string) => {
    const locale = isRTL ? 'he-IL' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Group materials by session
  const materialsBySession = sessions.reduce((acc, session) => {
    if (session.study_materials && session.study_materials.length > 0) {
      acc[session.id] = {
        sessionTitle: session.title,
        sessionDate: session.created_at,
        aiSummary: session.ai_generated_description,
        materials: session.study_materials
      };
    }
    return acc;
  }, {} as Record<number, { sessionTitle: string; sessionDate: string; aiSummary?: string; materials: StudyMaterial[] }>);

  const getMaterialTypeIcon = (type: string) => {
    switch (type) {
      case 'detailed_summary':
        return <FileText className="w-4 h-4 text-vibrant-orange" />;
      case 'short_lesson_timeline':
        return <Clock className="w-4 h-4 text-vibrant-orange" />;
      default:
        return <Sparkles className="w-4 h-4 text-vibrant-orange" />;
    }
  };

  const getMaterialTypeLabel = (type: string) => {
    switch (type) {
      case 'detailed_summary':
        return isRTL ? 'סיכום מפורט' : 'Detailed Summary';
      case 'short_lesson_timeline':
        return isRTL ? 'ציר זמן' : 'Timeline';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Card className="p-8 text-center max-w-md mx-auto constellation-orion">
            <LoadingSpinner size="lg" className="mx-auto mb-4" />
            <CardTitle className="text-surface-primary mb-2 font-hebrew">
              <MixedText>{t('courses:chat.loading')}</MixedText>
            </CardTitle>
          </Card>
        </div>
      </PageContainer>
    );
  }

  if (error || !course) {
    return (
      <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <p className="text-surface-secondary mb-6">{error || t('errors:something_went_wrong')}</p>
            <button
              onClick={() => navigate(`/course/${courseId}`)}
              className="text-vibrant-orange hover:text-vibrant-orange/80 underline font-hebrew"
            >
              <MixedText>{t('common:actions.back')}</MixedText>
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="2xl" spacing="normal" backgroundIntensity="none" showParticles={false}>
      {/* Header */}
      <div className="mb-8">
        {/* Breadcrumb Navigation */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/course/${courseId}`)}
            className={`flex items-center text-surface-secondary hover:text-vibrant-orange transition-colors group font-hebrew ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
          >
            {isRTL ? (
              <ArrowLeft className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200 ml-3 mr-3" />
            ) : (
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
            )}
            <span className={`font-medium ${isRTL ? 'text-right' : ''}`}>
              <MixedText className="text-bg-secondary hover:text-vibrant-orange transition-colors">{t('sessions:complete_step.course_overview.back_to_course')}</MixedText>
            </span>
          </button>
        </div>

        {/* Course Header */}
        <Card className="p-6">
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-4' : 'space-x-4'}`}>
              <div className="w-16 h-16 bg-vibrant-orange/10 rounded-2xl flex items-center justify-center">
                <MessageCircle className="w-8 h-8 text-vibrant-orange" />
              </div>
              <div className={`${isRTL ? 'text-right' : ''}`}>
                <h1 className={`text-2xl font-bold text-surface-primary mb-1 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{course?.name || t('courses:chat.title')}</MixedText>
                </h1>
                <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('courses:chat.description')}</MixedText>
                </p>
              </div>
            </div>

            {/* Materials Count Info */}
            <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
              <div className={`text-2xl font-bold text-vibrant-orange font-hebrew`}>
                {allMaterials.length}
              </div>
              <div className={`text-sm text-surface-secondary font-hebrew`}>
                <MixedText>{isRTL ? 'חומרי למידה' : 'Study Materials'}</MixedText>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Main Chat Card */}
      <div className="space-y-6">
        <Card className="chat-card-container">
          <CardContent className="relative p-0" style={{ height: 'calc(100vh - 250px)', minHeight: '600px' }}>
            {/* Chat container with relative positioning to contain the ChatWidget */}
            <div className="relative w-full h-full overflow-hidden rounded-lg">
              <ChatWidget
                sessionId={parseInt(courseId!)}
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
                chatHistory={chatHistory}
                onChatUpdate={handleChatUpdate}
                messageFeedback={messageFeedback}
                onMessageFeedback={handleMessageFeedback}
                customChatHandler={customChatHandler}
                isRTL={isRTL}
                mode={isVip ? 'smart' : chatMode}
                onModeChange={isVip ? undefined : handleChatModeChange}
                enableModeSwitch={!isVip}
                showCloseButton={false}
                allowResize={false}
                showMinimizeButton={false}
                embedded
              />
              
              {/* Style override to make ChatWidget fill the card */}
              <style>{`
                .chat-card-container .fixed {
                  position: absolute !important;
                  inset: 0 !important;
                  width: 100% !important;
                  height: 100% !important;
                  bottom: 0 !important;
                  right: 0 !important;
                  margin: 0 !important;
                  max-width: none !important;
                  border-radius: 0 !important;
                }
              `}</style>
            </div>
          </CardContent>
        </Card>

        {/* Materials Card */}
        <Card>
          <CardHeader>
            <CardTitle className={`text-xl font-bold font-hebrew ${isRTL ? 'text-right' : ''}`}>
              <span className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                <BookOpen className="w-5 h-5" />
                <MixedText>{t('courses:chat.materials_panel')}</MixedText>
              </span>
            </CardTitle>
          </CardHeader>

          <CardContent>
            {allMaterials.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-surface-muted/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <FileText className="w-8 h-8 text-surface-muted" />
                </div>
                <h3 className={`text-lg font-semibold text-surface-primary mb-2 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{t('courses:chat.no_materials')}</MixedText>
                </h3>
                <p className={`text-surface-secondary font-hebrew ${isRTL ? 'text-right' : ''}`}>
                  <MixedText>{isRTL ? 'עדיין לא נוצרו חומרי למידה לקורס זה' : 'No study materials generated for this course yet'}</MixedText>
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(materialsBySession).map(([sessionId, sessionData]) => (
                  <Card key={sessionId} className="group hover:shadow-lg transition-all duration-200">
                    <CardContent className="p-4">
                      <div className={`flex items-start ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'}`}>
                        <div className="w-10 h-10 bg-surface-muted/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FileText className="w-5 h-5 text-vibrant-orange" />
                        </div>
                        <div className={`flex-1 min-w-0 ${isRTL ? 'text-right' : ''}`}>
                          <h4 className={`font-medium text-surface-primary truncate mb-1 ${isRTL ? 'text-right' : ''}`}>
                            <MixedText>{sessionData.sessionTitle}</MixedText>
                          </h4>
                          <p className={`text-sm text-surface-secondary ${isRTL ? 'text-right' : ''}`}>
                            {formatDate(sessionData.sessionDate)}
                          </p>
                          {sessionData.aiSummary && (
                            <p className={`text-sm text-surface-primary font-medium mt-2 line-clamp-2 ${isRTL ? 'text-right' : ''}`}>
                              <MixedText>{sessionData.aiSummary}</MixedText>
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="mt-4 space-y-2">
                        {sessionData.materials.map((material) => (
                          <button
                            key={material.id}
                            onClick={() => setSelectedMaterial(material)}
                            className={`w-full p-3 rounded-lg border transition-all duration-200 hover:bg-vibrant-orange/5 hover:border-vibrant-orange/30 ${
                              selectedMaterial?.id === material.id 
                                ? 'border-vibrant-orange bg-vibrant-orange/5' 
                                : 'border-surface-muted/30 bg-white'
                            } ${isRTL ? 'text-right' : 'text-left'}`}
                          >
                            <div className={`flex items-center ${isRTL ? 'flex-row-reverse space-x-reverse space-x-2' : 'space-x-2'}`}>
                              <div className="w-8 h-8 bg-surface-muted/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                {getMaterialTypeIcon(material.material_type)}
                              </div>
                              <span className={`text-sm font-medium text-surface-primary ${isRTL ? 'text-right' : ''}`}>
                                {getMaterialTypeLabel(material.material_type)}
                              </span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Selected Material Modal */}
      {selectedMaterial && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedMaterial(null)}
        >
          <Card 
            className="w-full max-w-4xl max-h-[90vh] shadow-2xl shadow-vibrant-orange/20 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              {/* Header */}
              <div className={`flex items-center mb-6 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <div className={`flex items-center flex-1 ${isRTL ? 'flex-row-reverse space-x-reverse space-x-3' : 'space-x-3'}`}>
                  <div className="w-10 h-10 bg-vibrant-orange/10 rounded-lg flex items-center justify-center flex-shrink-0">
                    {getMaterialTypeIcon(selectedMaterial.material_type)}
                  </div>
                  <h2 className={`text-2xl font-bold text-vibrant-orange flex-1 min-w-0 font-hebrew ${isRTL ? 'text-right' : ''}`}>
                    <MixedText>{getMaterialTypeLabel(selectedMaterial.material_type)}</MixedText>
                  </h2>
                </div>
                <button
                  onClick={() => setSelectedMaterial(null)}
                  className="p-2 hover:bg-surface-muted/10 rounded-xl transition-colors duration-200 flex-shrink-0"
                  aria-label={isRTL ? 'סגור' : 'Close'}
                >
                  <svg className="w-6 h-6 text-surface-secondary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="overflow-y-auto max-h-[calc(90vh-140px)] -mx-2 px-2">
                <StudyMaterialRenderer
                  content={selectedMaterial.content}
                  materialType={selectedMaterial.material_type}
                  isRTL={isRTL}
                />
              </div>
            </div>
          </Card>
        </div>
      )}
    </PageContainer>
  );
};

export default CourseChatView;
